import { z } from 'zod';
import { FIELD_CANNOT_BE_EMPTY } from '../../../../../consts/validationErrors';
import { MAX_STEP_RESPONSIBLES } from '../EditActionPlanForm/helpers/actionPlanConstants';

const projectStepStatusSchema = z.enum([
  'draft',
  'awaiting',
  'in_progress',
  'paused',
  'canceled',
  'done',
] as const);

const responsibleOptionSchema = z.object({
  id: z.number(),
  label: z.string().trim().min(1, { message: FIELD_CANNOT_BE_EMPTY }),
});

const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  
  const parts = dateStr.split('.');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; 
  const year = parseInt(parts[2], 10);

  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  
  return isNaN(date.getTime()) ? null : date;
};

export const actionPlanSchema = z.object({
  actionPlan: z
    .array(
      z.object({
        stepId: z.number().optional(),
        status: projectStepStatusSchema.optional(),
        title: z
          .string({ required_error: FIELD_CANNOT_BE_EMPTY })
          .trim()
          .min(1, { message: FIELD_CANNOT_BE_EMPTY })
          .min(2, { message: "Минимально 2 символа" })
          .max(240, { message: "Максимально 240 символа" }),
        date_from: z
          .string({ required_error: FIELD_CANNOT_BE_EMPTY })
          .trim()
          .min(1, { message: FIELD_CANNOT_BE_EMPTY })
          .refine((value) => parseDate(value) !== null, { message: "Указана несуществующая дата" }),
        date_to: z
          .string({ required_error: FIELD_CANNOT_BE_EMPTY })
          .trim()
          .min(1, { message: FIELD_CANNOT_BE_EMPTY })
          .refine((value) => parseDate(value) !== null, { message: "Указана несуществующая дата" }),
        description: z
          .string({ required_error: FIELD_CANNOT_BE_EMPTY })
          .trim()
          .min(1, { message: FIELD_CANNOT_BE_EMPTY })
          .min(2, { message: "Минимально 2 символа" })
          .max(240, { message: "Максимально 240 символа" }),
        responsibles: z
          .array(responsibleOptionSchema)
          .min(1, { message: FIELD_CANNOT_BE_EMPTY })
          .max(MAX_STEP_RESPONSIBLES, { message: `Максимум ${MAX_STEP_RESPONSIBLES} ответственных` }),
      })
    )
    .superRefine((items, ctx) => {
      if (!items || items.length === 0) return;

      const projectStartDate = parseDate(items[0].date_from.trim());

      items.forEach((item, index) => {
        const startDate = parseDate(item.date_from.trim());
        const endDate = parseDate(item.date_to.trim());

        if (index > 0 && projectStartDate && startDate && startDate < projectStartDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Дата не может быть раньше начала всего плана",
            path: [index, "date_from"],
          });
        }

        if (startDate && endDate && endDate <= startDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Дата окончания должна быть позже даты начала",
            path: [index, "date_to"],
          });
        }
      });
    }),
});


export type TActionPlanFormValues = z.output<typeof actionPlanSchema>
export type TActionPlanItem = TActionPlanFormValues["actionPlan"][number]
